import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {AbstractControl, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {Subscription} from 'rxjs';
import {BusinessDataService} from '../../../../../data-repositories/business-data.service';
import {KnowledgeLevelData} from '../../../../../data-repositories/knowledge-level-data';
import {LanguageService} from '../../../../../data-repositories/language.service';
import {MoneyRangeData, MoneyRangeValue} from '../../../../../data-repositories/money-range-data';
import {Assets} from '../../../../../models/assets';
import {Investment} from '../../../../../models/investment';
import {InvestmentObjective} from '../../../../../models/investment-objectiv';
import {User} from '../../../../../models/user';
import {ValidationInfoService} from '../../../../../repositories/validation-info.service';
import {FocusInvalidService} from '../../../../../services/focus-invalid.service';
import {StagesNavigationService} from '../../../stages-navigation/stages-navigation.service';
import {ActivatedRoute} from '@angular/router';
import {MonitoringType} from '../../../../../models/monitoring-type';
import {MarginTypeService} from '../../../../../services/margin-type.service';
import {formLog} from '../../../../../services/debug-logger';

@Component({
  standalone: false,
  selector: 'app-experience',
  templateUrl: './experience.component.html',
  styleUrls: ['./experience.component.scss']
})
export class ExperienceComponent implements OnInit, OnDestroy {

  public yearsTradingList: Array<MoneyRangeValue>;
  public tradesPerYearList: Array<MoneyRangeValue>;
  public knowledgeLevelList: Array<string>;

  public investmentModel: Investment = new Investment();
  public assetsModel: Assets = new Assets();
  public userModel: User = new User();

  public productsForm: FormGroup;
  public submitted: boolean;
  public knowledgeInfoIsOpen: boolean;
  public knowledgeInfo: string;
  public objectivesInfoIsOpen: boolean;
  public objectivesInfo: string;
  public marginExperienceInfoIsOpen: boolean;
  public marginExperienceInfo: string;
  @ViewChild('productsFormElement') productsFormElement: ElementRef;
  initial: boolean;
  private subscription: Subscription;
  private formSub = new Subscription();

  constructor(
    private service: BusinessDataService,
    private formBuilder: FormBuilder,
    private moneyRangeData: MoneyRangeData,
    private knowledgeLevelData: KnowledgeLevelData,
    private validationService: ValidationInfoService,
    public langService: LanguageService,
    private focusInvalidService: FocusInvalidService,
    public navigationService: StagesNavigationService,
    private marginTypeService: MarginTypeService) {

    this.yearsTradingList = moneyRangeData.getYearsTradingData();
    this.tradesPerYearList = moneyRangeData.getTradingsPerYear();
    this.knowledgeLevelList = knowledgeLevelData.getKnowledgeLevelList();
    this.createForm();

    this.submitted = false;

    this.formSub.add(this.service.GetUser().subscribe(s => {
      this.initial = s.initial;
      this.investmentModel = s.model.investmentModel;
      this.assetsModel = s.model.assetsModel;
      this.userModel = s.model;
      this.setDefaultValues(this.investmentModel);
      this.patchFormFromModel();
    }));

    this.formSub.add(this.langService.getInfoFieldSubject().subscribe(ifm => {
      this.knowledgeInfo = ifm.find(field => field.key === 'Knwoledge And Experience').info;
      this.objectivesInfo = ifm.find(field => field.key === 'Investment Objectives').info;
      this.marginExperienceInfo = ifm.find(field => field.key === 'Margin experience').info;
    }));
  }

  ngOnInit() {
    this.patchFormFromModel();
    this.formSub.add(this.productsForm.valueChanges.subscribe(() => this.syncModelFromForm()));

    this.subscription = this.validationService.onUserUpdate.subscribe(() => {
      this.submitted = true;

      let isValid: boolean;
      isValid = this.productsForm.valid && this.isValidObjectives();
      this.markAllFieldsAsTouched();

      this.validationService.validate(isValid, 'ExperienceComponent');
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.formSub.unsubscribe();
  }

  public setYearOfExperience(objective: InvestmentObjective, objectiveName: string, years: number) {
    if (Number(objective.yearsTrading) < years) {
      objective.isObjective = false;
      objective.knowledgeLevel = '';
      objective.tradesPerYear = '';

      this.removeValidators(objectiveName);

      this.productsForm.controls[objectiveName + 'Years'].setErrors({lowExperience: true});
    } else {
      if (!objective.knowledgeLevel) {
        objective.knowledgeLevel = '';
      }
      if (!objective.tradesPerYear) {
        objective.tradesPerYear = '';
      }
      if (!objective.yearsTrading) {
        objective.yearsTrading = '';
      }
      objective.isObjective = true;
      this.productsForm.controls[objectiveName + 'TradesPerYear'].setValidators(Validators.required);
      this.productsForm.controls[objectiveName + 'TradesPerYear'].setErrors(objective.tradesPerYear === '' ? {required: true} : null);

      this.productsForm.controls[objectiveName + 'Knowledge'].setValidators([Validators.required, validKnowledge()]);
      this.productsForm.controls[objectiveName + 'Knowledge'].updateValueAndValidity();
    }

    if (objective.marginExperience == null) {
      objective.marginExperience = true;
    }

    if (this.initial) {
      this.updateUser();
    }
  }

  public checkObjectiveExperience(objective: InvestmentObjective, years: number): boolean {
    if (objective.yearsTrading && Number(objective.yearsTrading) >= years) {
      return true;
    }
  }

  public isLowExperience(objectiveName: string): boolean {
    return this.productsForm.controls[objectiveName + 'Years'].errors
      && this.productsForm.controls[objectiveName + 'Years'].errors.lowExperience;
  }

  public isLowKnowledge(objectiveName: string): boolean {
    return this.productsForm.controls[objectiveName + 'Knowledge'].errors
      && this.productsForm.controls[objectiveName + 'Knowledge'].errors.lowKnowledge;
  }

  public isCashAccount(objectiveName: string): boolean {
    const stocks = this.userModel.investmentModel.stocks;
    const monitoringIE = this.userModel.monitoring === MonitoringType.Ireland;

    const fieldsFilled =
      stocks.yearsTrading !== '' &&
      stocks.knowledgeLevel !== '';

    const yearsLow = +stocks.yearsTrading < 2;
    const knowledgeLow = stocks.knowledgeLevel === 'Limited';
    const noMarginExperience = !stocks.marginExperience;

    const isCash = yearsLow || knowledgeLow || noMarginExperience;

    return monitoringIE && fieldsFilled && isCash;
  }

  public removeObjective(objective: InvestmentObjective, objectiveName: string) {
    objective.isObjective = false;
    objective.yearsTrading = '';
    objective.knowledgeLevel = '';
    objective.tradesPerYear = '';

    this.removeValidators(objectiveName);
    this.productsForm.controls[objectiveName + 'Years'].setErrors(null);
  }

  stocksIsValid(): boolean {
    if (!this.investmentModel.stocks.yearsTrading) {
      return true;
    }

    const valid = (this.investmentModel.growth
        || this.investmentModel.tradingProfits
        || this.investmentModel.speculation
        || this.investmentModel.hedging)
      && ((Number(this.assetsModel.liquidNetWorth) >= 2 && Number(this.assetsModel.netInCome) >= 2)
        || Number(this.assetsModel.liquidNetWorth) > 4);

    return valid;
  }

  isValidObjectives(): boolean {
    let objectivesAmount = 0;
    // if (this.investmentModel.preservationofCapital) {
    //   objectivesAmount++;
    // }
    if (this.investmentModel.growth) {
      objectivesAmount++;
    }
    if (this.investmentModel.speculation) {
      objectivesAmount++;
    }
    // if (this.investmentModel.income) {
    //   objectivesAmount++;
    // }
    if (this.investmentModel.hedging) {
      objectivesAmount++;
    }
    if (this.investmentModel.tradingProfits) {
      objectivesAmount++;
    }

    return objectivesAmount >= 2;
  }

  public updateUser(): void {
    this.service.updateUserWithoutValidation(this.userModel, () => {
    });
  }

  getMarginType(checkTrades: boolean = true) {
    return this.marginTypeService.getMarginType(this.userModel, checkTrades, false);
  }

  marginExperience(checkTrades: boolean = true) {
    return this.getMarginType(checkTrades) === 'Margin' && this.userModel.monitoring === MonitoringType.Ireland;
  }

  isValidTotalAmountOfTrades(): boolean {
    return this.investmentModel.stocks.tradesPerYear ? !this.marginExperience(false) ||
      (+this.investmentModel.stocks.yearsTrading * +this.investmentModel.stocks.tradesPerYear > 20) : true;
  }

  public isRequiredFields(): boolean {
    if (!this.productsForm) {
      return false;
    }

    return Object.keys(this.productsForm.controls).some(fieldName => {
      const field = this.productsForm.controls[fieldName];
      return field.invalid && (field.dirty || field.touched) && field.errors.required;
    });
  }

  private setDefaultValues(model: Investment) {
    this.setYearOfExperience(model.stocks, 'stocks', 0);
  }

  private removeValidators(objectiveName: string) {
    this.productsForm.controls[objectiveName + 'TradesPerYear'].setValidators(null);
    this.productsForm.controls[objectiveName + 'TradesPerYear'].setErrors(null);

    this.productsForm.controls[objectiveName + 'Knowledge'].setValidators(null);
    this.productsForm.controls[objectiveName + 'Knowledge'].setErrors(null);
  }

  private patchFormFromModel(): void {
    if (!this.investmentModel || !this.productsForm) {
      return;
    }

    this.productsForm.patchValue({
      stocksYears: this.investmentModel.stocks.yearsTrading ?? '',
      stocksTradesPerYear: this.investmentModel.stocks.tradesPerYear ?? '',
      stocksKnowledge: this.investmentModel.stocks.knowledgeLevel ?? '',
    }, {emitEvent: false});
    formLog.state('Experience', this.investmentModel, this.productsForm);
  }

  private syncModelFromForm(): void {
    if (!this.investmentModel) {
      return;
    }

    const value = this.productsForm.getRawValue();
    this.investmentModel.stocks.yearsTrading = value.stocksYears;
    this.investmentModel.stocks.tradesPerYear = value.stocksTradesPerYear;
    this.investmentModel.stocks.knowledgeLevel = value.stocksKnowledge;
    formLog.state('Experience', this.investmentModel, this.productsForm);
  }

  private createForm() {
    this.productsForm = this.formBuilder.group({
      stocksYears: new FormControl('', [Validators.required]),
      stocksTradesPerYear: new FormControl('', [Validators.required]),
      stocksKnowledge: new FormControl('', [Validators.required, validKnowledge()]),
    });
  }

  private markAllFieldsAsTouched(): void {
    Object.keys(this.productsForm.controls).forEach(key => {
      this.productsForm.get(key).markAsTouched();
    });
  }
}

export function validKnowledge(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const forbidden = control.value === 'Limited';
    return forbidden ? {lowKnowledge: true} : null;
  };
}
